import { Dive, DivePoint, DiveStats } from '../types/Dive';
import { API_BASE_URL } from './api';

const authHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const jsonAuthHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  ...authHeaders(),
});

/** The objective facts of a dive — what transfers to a buddy. Personal gear/gas data does not. */
export interface SharedDiveSnapshot {
  startTime: string;
  endTime: string;
  duration: string;
  maxDepth: number;
  avgDepth: number;
  waterTemperature: number;
  diveType: string;
  location: string;
  latitude?: number;
  longitude?: number;
  diveProfile: DivePoint[];
  stats: DiveStats;
}

export interface IncomingShare {
  shareId: string;
  ownerUserId: string;
  ownerDisplayName: string;
  diveLabel: string;
  snapshot: SharedDiveSnapshot;
  createdAt: string;
  /** True when this dive is already in the recipient's log. */
  alreadyLogged: boolean;
}

export interface OutgoingShare {
  shareId: string;
  targetUserId?: string;
  targetDisplayName: string;
  status: 'Pending' | 'Accepted' | 'Declined' | 'Revoked';
  shareCode?: string;
  createdAt: string;
  expiresAt?: string;
}

export interface ShareDiveResult {
  success: boolean;
  sharedCount: number;
  /** Buddy ids that were skipped — not an accepted buddy, or already shared with. */
  skipped: string[];
  shareCode?: string;
}

/** Unauthenticated preview of a share link, before the visitor signs in. */
export interface SharePreview {
  ownerDisplayName: string;
  diveLabel: string;
  snapshot: SharedDiveSnapshot;
  createdAt: string;
  alreadyClaimed: boolean;
}

const errorFrom = async (res: Response, fallback: string): Promise<string> => {
  try {
    const body = await res.json();
    return body?.error || fallback;
  } catch {
    return fallback;
  }
};

export const diveShareService = {
  shareDive: async (
    diveId: string,
    buddyUserIds: string[],
    createLink = false,
  ): Promise<ShareDiveResult> => {
    const res = await fetch(`${API_BASE_URL}/dives/shares/dive/${encodeURIComponent(diveId)}`, {
      method: 'POST',
      headers: jsonAuthHeaders(),
      body: JSON.stringify({ buddyUserIds, createLink }),
    });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to share dive'));
    return res.json();
  },

  getIncoming: async (): Promise<IncomingShare[]> => {
    const res = await fetch(`${API_BASE_URL}/dives/shares/incoming`, { headers: authHeaders() });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to load shared dives'));
    return res.json();
  },

  getOutgoing: async (diveId: string): Promise<OutgoingShare[]> => {
    const res = await fetch(`${API_BASE_URL}/dives/shares/outgoing/${encodeURIComponent(diveId)}`, {
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to load shares for this dive'));
    return res.json();
  },

  accept: async (shareId: string): Promise<Dive> => {
    const res = await fetch(`${API_BASE_URL}/dives/shares/${encodeURIComponent(shareId)}/accept`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to accept shared dive'));
    const body = await res.json();
    return body.dive;
  },

  decline: async (shareId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/dives/shares/${encodeURIComponent(shareId)}/decline`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to decline shared dive'));
  },

  revoke: async (shareId: string): Promise<void> => {
    const res = await fetch(`${API_BASE_URL}/dives/shares/${encodeURIComponent(shareId)}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to revoke share'));
  },

  /** No auth — powers the signed-out preview of a share link. */
  previewByCode: async (shareCode: string): Promise<SharePreview> => {
    const res = await fetch(`${API_BASE_URL}/dives/shares/code/${encodeURIComponent(shareCode)}`);
    if (!res.ok) throw new Error(await errorFrom(res, 'This share link is not valid'));
    return res.json();
  },

  claimByCode: async (shareCode: string): Promise<Dive> => {
    const res = await fetch(`${API_BASE_URL}/dives/shares/code/${encodeURIComponent(shareCode)}/claim`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to add this dive'));
    const body = await res.json();
    return body.dive;
  },

  /** Co-log the dive behind a DiveLog post you were tagged in — no prior share step needed. */
  coLogFromPost: async (postId: string): Promise<Dive> => {
    const res = await fetch(`${API_BASE_URL}/dives/shares/from-post/${encodeURIComponent(postId)}`, {
      method: 'POST',
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await errorFrom(res, 'Failed to add this dive to your log'));
    const body = await res.json();
    return body.dive;
  },
};
